import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface EmptyStateProps {
  /**
   * Icon to display (Lucide icon component)
   */
  icon?: React.ReactNode
  /**
   * Title - what's missing (e.g., "No projects yet")
   */
  title: string
  /**
   * Description explaining what this section will contain
   */
  description?: string
  /**
   * Primary action button label
   */
  actionLabel?: string
  /**
   * Callback when action button is clicked
   */
  onAction?: () => void
  /**
   * Additional class names
   */
  className?: string
}

/**
 * Empty state component following PRD guidelines.
 *
 * Structure:
 * - Relevant illustration/icon
 * - Clear title ("No [items] yet")
 * - Brief explanation
 * - Primary CTA button
 *
 * @example
 * <EmptyState
 *   icon={<FolderIcon className="h-12 w-12" />}
 *   title="No projects yet"
 *   description="Create your first project to get started"
 *   actionLabel="Create Project"
 *   onAction={() => navigate('/projects/new')}
 * />
 */
export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center py-12 px-4 text-center', className)}
    >
      {/* Icon/Illustration */}
      {icon && <div className="mb-4 text-muted-foreground opacity-50">{icon}</div>}

      {/* Default illustration if no icon provided */}
      {!icon && (
        <div className="mb-4 text-muted-foreground opacity-50">
          <svg
            className="h-16 w-16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1}
            aria-hidden="true"
            role="presentation"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
        </div>
      )}

      {/* Title */}
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>

      {/* Description */}
      {description && <p className="text-sm text-muted-foreground max-w-sm mb-6">{description}</p>}

      {/* Action Button */}
      {actionLabel && onAction && <Button onClick={onAction}>{actionLabel}</Button>}
    </div>
  )
}
